import {
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Squish,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { Input } from 'src/components/Input/Input';
import Table from 'src/components/Table/Table';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import { ILineOfCreditResponseType } from 'src/types/LineOfCredit';
import AddGroupsToLocModal from 'src/components/selfService/features/LineOfCredit/details/Groups/AddGroupsToLocModal';
import { useLocGroupsManagement } from 'src/components/selfService/features/LineOfCredit/details/Groups/hooks/useLocGroupsManagement';
import { useFilterTagInvites } from 'src/common/features/LineOfCredit/hooks/useFilterTagInvites';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useCheckboxSelection } from 'src/components/People/Groups/hooks/useCheckboxSelection';
import { getGroupIdsFromLocList } from 'src/components/selfService/features/LineOfCredit/LineOfCreditLanding.utils';
import { useFetchDinerCountPerGroup } from 'src/components/selfService/features/LineOfCredit/hooks/useFetchDinerCountPerGroup';
import { getSingularOrPlural } from 'src/utilities/getSingularOrPlural';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import GroupLocTableItem from './GroupLocTableItem';

const FIXED_BODY_HEIGHT = '300px';

export interface IRemoveGroupModalStatus {
  showModal: boolean;
  numberOfGroupsToRemove: number;
  groupId: string;
}

export interface IGroupLocTableProps {
  loc: ILineOfCreditResponseType;
  corpId: string;
  onAddGroupsSuccess: () => void;
  onRemoveGroupsSuccess: () => void;
}

export default function GroupLocTable({
  loc,
  corpId,
  onAddGroupsSuccess,
  onRemoveGroupsSuccess,
}: IGroupLocTableProps): JSX.Element {
  const { isPhone, isDesktop } = useScreenSizes();
  const { MEAL_CREDIT_ENTITY_NAME } = useMealCreditEntityName();
  const {
    initialTagInvitesLength,
    filteredTagInvites,
    isUsingQuery,
    filterTagInvitesBySearch,
  } = useFilterTagInvites({
    loc,
  });
  const [showAddGroupsModal, setShowAddGroupsModal] = useState(false);

  const { sendAnalytics, sendAnalyticsBefore } = useSendAnalytics({
    eventcategory: 'self-service management tool_loc',
  });

  const [groupRemovalModalStatus, setGroupRemovalModalStatus] =
    useState<IRemoveGroupModalStatus>({
      showModal: false,
      numberOfGroupsToRemove: 0,
      groupId: null,
    });

  const { mutate: addGroupsMutate, isLoading: isAddGroupsLoading } =
    useLocGroupsManagement({
      action: 'add',
      loc,
      corpId,
      onSuccess: () => {
        setShowAddGroupsModal(false);
        onAddGroupsSuccess();
        sendAnalytics({
          eventaction: 'add groups to loc_cta',
        });
      },
    });

  const {
    isSelected,
    toggleAll,
    toggleItem,
    numberOfSelectedItems,
    selectedItems,
  } = useCheckboxSelection(filteredTagInvites, 'id');

  const { mutate: removeGroupsMutate, isLoading: isRemoveGroupsLoading } =
    useLocGroupsManagement({
      action: 'remove',
      loc,
      corpId,
      onSuccess: () => {
        if (!groupRemovalModalStatus.groupId) {
          toggleAll(false);
        }
        closeGroupRemovalConfirmDialog();
        onRemoveGroupsSuccess();
      },
    });

  const groupIds = getGroupIdsFromLocList([loc]);

  const { data: dinerCountByGroupMap, isLoading } = useFetchDinerCountPerGroup(
    groupIds,
    corpId
  );

  const existingGroupsMap = useMemo<Record<string, boolean>>(() => {
    if (!Array.isArray(loc?.tag_invites)) {
      return {};
    }

    return loc.tag_invites.reduce((acc, group) => {
      acc[group.id] = true;

      return acc;
    }, {});
  }, [loc.tag_invites]);

  function openGroupRemovalConfirmDialog(group: number, groupId: string) {
    setGroupRemovalModalStatus({
      showModal: true,
      numberOfGroupsToRemove: group,
      groupId,
    });
  }

  function closeGroupRemovalConfirmDialog() {
    setGroupRemovalModalStatus({
      showModal: false,
      numberOfGroupsToRemove: 0,
      groupId: null,
    });
  }

  function removeGroup(groupId: string) {
    const groupsToRemove = groupId ? [groupId] : selectedItems;
    removeGroupsMutate(groupsToRemove);
  }

  const handleAssignGroupClick = () => {
    setShowAddGroupsModal(true);
  };

  const groupsText = getSingularOrPlural(numberOfSelectedItems, 'group');
  const numberOfGroupsText = `${numberOfSelectedItems} ${groupsText} selected`;

  return (
    <>
      <Squish inset={!isPhone ? 'lg' : 'md'}>
        <Flex
          flexDirection={isPhone ? 'column' : 'row'}
          justifyContent="space-between"
        >
          <Flex flexBasis={!isPhone ? '248px' : 'auto'}>
            <Stack
              y={isPhone ? 'md' : 'none'}
              x={!isPhone ? 'md' : 'none'}
              width="100%"
            >
              <Input
                key="searchTerm"
                ariaLabel="Search by group name"
                placeholder="Search by group name"
                name="searchTerm"
                id="searchTerm"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  filterTagInvitesBySearch(event?.target.value)
                }
                prefixIcon={
                  <Copy variant="body" color="secondaryText">
                    <SvgMagnifyingGlass size="16px" />
                  </Copy>
                }
              />
            </Stack>
          </Flex>

          <Button
            variant="primary"
            buttonSize="medium"
            onClick={() =>
              sendAnalyticsBefore(handleAssignGroupClick, {
                eventaction: 'add groups to loc_impression',
              })
            }
          >
            Assign group
          </Button>
        </Flex>
      </Squish>
      <Squish position="relative">
        <Table
          isLoading={isAddGroupsLoading || isRemoveGroupsLoading}
          hideOutsideBorder
          hideHeader={false}
          bodyMinHeight={FIXED_BODY_HEIGHT}
          bodyMaxHeight={isDesktop ? FIXED_BODY_HEIGHT : 'auto'}
          headerInsets={!isPhone ? 'lg' : 'md'}
          header={() => (
            <>
              <Flex alignItems="center">
                <Stack x="x-sm">
                  <Checkbox
                    id="select-all"
                    name="select-all"
                    ariaLabel="select-all"
                    cancelLabelBottomMargin
                    checked={!!numberOfSelectedItems}
                    onChange={() => toggleAll()}
                    isMultipleSelection
                  />
                </Stack>
                <Stack flex={1}>
                  <Row alignItems="center">
                    <Column desktop={9} tablet={9} phone={8}>
                      {isDesktop ? (
                        <Row>
                          <Column desktop={4}>
                            <Copy variant="body" color="secondaryText">
                              Group name
                            </Copy>
                          </Column>
                          <Column desktop={8}>
                            <Copy variant="body" color="secondaryText">
                              No. of people
                            </Copy>
                          </Column>
                        </Row>
                      ) : (
                        <Row>
                          <Column desktop={12}>
                            <Copy variant="body" color="secondaryText">
                              Group details
                            </Copy>
                          </Column>
                        </Row>
                      )}
                    </Column>
                    <Column desktop={3} tablet={3} phone={4}>
                      <Row justifyContent="flex-end">
                        <Copy variant="body" color="secondaryText">
                          {`${filteredTagInvites.length} of ${initialTagInvitesLength} groups`}
                        </Copy>
                      </Row>
                    </Column>
                  </Row>
                </Stack>
              </Flex>
            </>
          )}
          body={() =>
            filteredTagInvites.length > 0 ? (
              <Stack as="ul" className="removeListStyles">
                {filteredTagInvites.map((group) => (
                  <GroupLocTableItem
                    key={group.id}
                    group={group}
                    dinerCount={
                      !isLoading ? dinerCountByGroupMap?.[group.id] : null
                    }
                    checked={isSelected(group.id)}
                    onCheckboxChange={toggleItem}
                    onGroupRemove={() => {
                      openGroupRemovalConfirmDialog(1, group.id);
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <>
                {isUsingQuery && filteredTagInvites.length === 0 ? (
                  <NoResultsMsg message="We couldn’t find what you were looking for. Give it another go." />
                ) : (
                  <NoResultsMsg
                    message={`No groups have been given this ${MEAL_CREDIT_ENTITY_NAME.singular}`}
                  />
                )}
              </>
            )
          }
          justifyFooter="space-between"
          footerMinHeight="auto"
          footerBg="untintedBackground"
          showFooter={!!numberOfSelectedItems}
          footer={() => (
            <Squish inset="lg" width="100%">
              <Flex
                alignItems="center"
                justifyContent={isPhone ? 'center' : 'space-between'}
                flexDirection={isPhone ? 'column' : 'row'}
              >
                <Stack y={isPhone ? 'x-sm' : 'none'}>
                  <Copy variant="lead">{numberOfGroupsText}</Copy>
                </Stack>
                <Stack width={isPhone ? '100%' : 'auto'}>
                  <Button
                    block={isPhone}
                    variant="primary"
                    buttonSize="medium"
                    onClick={() => {
                      openGroupRemovalConfirmDialog(selectedItems.length, null);
                    }}
                  >
                    Remove
                  </Button>
                </Stack>
              </Flex>
            </Squish>
          )}
        />
      </Squish>

      {showAddGroupsModal && (
        <AddGroupsToLocModal
          isLoading={isAddGroupsLoading}
          corpId={corpId}
          onClose={() => setShowAddGroupsModal(false)}
          onAdd={addGroupsMutate}
          existingGroupsMap={existingGroupsMap}
        />
      )}

      {groupRemovalModalStatus.showModal && (
        <SimpleDialog
          isPrimaryButtonLoading={isRemoveGroupsLoading}
          primaryActionFlavor="danger"
          title={`${
            groupRemovalModalStatus.numberOfGroupsToRemove === 1
              ? `Remove this group?`
              : `Remove ${groupRemovalModalStatus.numberOfGroupsToRemove} groups?`
          } `}
          description={`After removing them, people in these groups will be removed from this ${MEAL_CREDIT_ENTITY_NAME.singular}.`}
          primaryActionText="Remove"
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          onPrimaryActionClick={() =>
            removeGroup(groupRemovalModalStatus.groupId)
          }
          onSecondaryActionClick={closeGroupRemovalConfirmDialog}
        />
      )}
    </>
  );
}
